import { GetResult } from "@/api/types";
import { Filter, Pagination, Search, Sort } from "@/components/Table/Table";
import { YearStatus } from "@/components/YearSelector/YearSelector";
import useSmartQueryClient from "@/hooks/useSmartQueryClient";
import { IImpact } from "@/pages/dashboard/utils/types";
import { filterSearchToGetFilter, GetData, sortToParam } from "@/utilities/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";

export function useGetBusinessTravelYears() {
  return useQuery({
    queryKey: ["employees", "business_travel"],
    queryFn: () =>
      axios.get<YearStatus[]>("/employees/business_travel/years").then((result) => result.data),
  });
}

export interface BusinessTravel {
  id: string;
  employee_id: string;
  date: string;
  departure: string;
  arrival: string;
  travel_mode: string;
  travel_mode_id: number;
  distance: number;
  owned: boolean;
  dataset_id?: number;
  impact: IImpact;
}

export interface GetBusinessTravel extends GetResult {
  data: BusinessTravel[];
}

export function useGetBusinessTravel({
  year,
  pagination,
  filter,
  search,
  sort,
  enabled,
}: {
  year?: number;
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ["employees", "business_travel", year, pagination, filter, sort, search],
    enabled: enabled ?? true,
    queryFn: () =>
      axios
        .get<GetData<BusinessTravel>>(`/employees/business_travel/${year}`, {
          params: {
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
          },
        })
        .then((result) => result.data),
  });
}

export function useDeleteBusinessTravel() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ year }: { year?: number }) =>
      axios.delete(`/employees/business_travel`, {
        params: {
          year,
        },
      }),
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["employees", "business_travel"]);
      smartQueryClient.invalidateQueries(["employees", "travel_modes"]);
    },
  });
}

export function useDeleteBusinessTravelAll() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: () => axios.delete(`/employees/business_travel`),
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["employees", "business_travel"]);
      smartQueryClient.invalidateQueries(["employees", "travel_modes"]);
    },
  });
}

export const exportBusinessTravel = async ({ year }: { year?: number }): Promise<string> => {
  const { data } = await axios.get<{ url: string }>(`/employees/business_travel/export`, {
    params: { ...(year ? { year } : {}) },
  });

  return data.url;
};
